import { ModalService } from "@/components/fpComponents/services/ModalService/ModalService";
import { MessageDataObject } from "./useIframeConnection";
import { useNewDealWithClientCreationModal } from "./useNewDealWithClientCreationModal";
import GoToService from "@/router/GoToService";
import ChangeOwnership from "@/modules/client/views/ownership/ChangeOwnership.vue";
import Client from "@/models/Client";
import User from "@/models/user/User";
import { computed } from "vue";
import { ProductType } from "@/commons/enums/ProductType";
import { useBreakpointsApi } from "@/composables/useBreakpoints";
import { SimulationDemand } from "@/models/Demand";
import { DealCreationMode } from "@/models/Deal";
import { useSimulationStore } from "@/components/calculator/services/SimulationStore";
import {
  IFRAME_MESSAGE_TYPES,
  type AnotherClientCreatedPayload,
  type ChangeOwnerPayload,
  type IframeModalType,
  type NewClientModalClosePayload,
  type NewDealModalClosePayload,
  iframeBridge,
} from "./iframeBridgeProtocol";
import { useIframeBridge } from "./useIframeBridge";
import { buildIframeQueryString } from "./iframeModalQuery";
import { usePreloadedIframe } from "./usePreloadedIframe";

export const useNewClientModal = (modalService?: ModalService) => {
  type AddClientModalParams = {
    goToCart?: boolean;
    keepClientData?: boolean;
    clientType?: "individual" | "company";
    anotherClient?: boolean;
    productType?: ProductType | null;
    parentModalType?: IframeModalType;
    userInput?: SimulationDemand;
    mode?: DealCreationMode;
    editingFields?: (keyof Client)[];
    /**
     * Deal flows (active-case assign, new-deal wizard) set this so the iframe
     * returns a clicked existing-client duplicate into the flow instead of
     * hard-redirecting to client details. Omitted for the standalone add-client.
     */
    selectExistingClient?: boolean;
    /**
     * "Assign to active case" flow: when the user picks an existing client
     * (one that already has an id), return it straight to the caller instead of
     * opening the deal-creation wizard. Newly-entered clients still go through
     * the wizard so their agreements get collected.
     */
    returnExistingClientImmediately?: boolean;
    onClientCreationClose?: (response?: Nullable<NewDealModalClosePayload>) => void;
  };

  const { openNewDealWithClientCreationModal, continueInPlace } =
    useNewDealWithClientCreationModal(modalService);

  type NewDealWithClientCreationResult = Awaited<
    ReturnType<typeof openNewDealWithClientCreationModal>
  >;

  const iframeMessageCallback = async (
    eventData: MessageDataObject<ChangeOwnerPayload | number | null | undefined>
  ) => {
    if (
      iframeBridge.toFincrm.newClient.goToClient.matches(eventData) &&
      eventData.data
    ) {
      GoToService.client(eventData.data);
    } else if (
      iframeBridge.toFincrm.newClient.changeOwner.matches(eventData) &&
      eventData.data?.client
    ) {
      if (!eventData.data.owner) {
        return;
      }
      const modalResponse = await modalService?.open(ChangeOwnership, {
        client: new Client(eventData.data.client),
        owner: new User(eventData.data.owner),
      });
      if (modalResponse) {
        GoToService.client(eventData.data.client.id);
      }
    }
  };

  /**
   * Which open modal, if any, can absorb the accepted client without a
   * close+reopen. `null` means the legacy path.
   */
  const resolveInPlaceParent = (params: AddClientModalParams) => {
    const parentModalType = params.parentModalType;
    if (
      !parentModalType ||
      params.mode === "EditClientData" ||
      (parentModalType !== IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL &&
        parentModalType !==
          IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL)
    ) {
      return null;
    }
    const { isVisible, currentModalName, childCapabilities } =
      usePreloadedIframe();
    const canContinue =
      isVisible.value &&
      currentModalName.value === parentModalType &&
      !!childCapabilities.value.inPlaceClientCreate;
    return canContinue ? parentModalType : null;
  };

  /** SaveOnDeal from the comparator needs the created deal back in the store. */
  const loadCreatedDealIntoSimulation = async (
    nextStepResponse: NewDealWithClientCreationResult
  ) => {
    if (
      nextStepResponse &&
      typeof nextStepResponse === "object" &&
      "deal" in nextStepResponse &&
      nextStepResponse.deal
    ) {
      const store = useSimulationStore();
      await store.init(
        store.productType || ProductType.MORTGAGE,
        store.simulationId,
        nextStepResponse.deal
      );
    }
  };

  const openAddClientModal = async (params: AddClientModalParams) => {
    const queryString = buildIframeQueryString({
      keepClientData: !!params.keepClientData,
      anotherClient: !!params.anotherClient,
      userInput: params?.userInput,
      editingFields: params?.editingFields,
      mode: params?.mode,
      selectExistingClient: params?.selectExistingClient,
    });
    const modalUrl = GoToService.getLendiV3Url(
      `clients/newClientModal${
        params.clientType ? `/${params.clientType}` : ""
      }${queryString}`,
      true
    );
    const modalResponse = (await modalService?.openIframeModal(
      {
        url: modalUrl,
        name: IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL,
        callback: iframeMessageCallback,
      },
      {
        persistent: true,
        maxWidth: 580,
        fullscreen: isMobile.value,
        contentClass: isMobile.value ? "" : "new-client-modal-content",
      }
    )) as NewClientModalClosePayload | null | undefined;

    if (modalResponse) {
      if (params?.anotherClient) {
        const { toLendiCrm } = useIframeBridge();
        toLendiCrm.newDealWithClientCreation.resumeAfterAdditionalClientCreated(
          modalResponse as AnotherClientCreatedPayload
        );
        return modalResponse;
      }

      // Existing client picked from the duplicate suggestion while assigning to
      // an active case: hand it straight back to the caller (which assigns it),
      // skipping the deal-creation wizard. Newly-entered clients have no id and
      // still fall through to the wizard so their agreements get collected.
      if (params?.returnExistingClientImmediately && modalResponse.id) {
        return modalResponse;
      }

      // Existing client picked while building a deal in the new-deal wizard:
      // select it straight into the still-open wizard (like the client picker)
      // instead of closing it and opening the create-client flow. The wizard
      // then handles agreements itself and skips the step when already given.
      if (
        modalResponse.id &&
        params?.parentModalType === IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL
      ) {
        const { toLendiCrm } = useIframeBridge();
        toLendiCrm.newDeal.selectExistingClient(modalResponse);
        return modalResponse;
      }

      const inPlaceParent = resolveInPlaceParent(params);

      // Back-loop re-accept: the open modal is already the client-creation
      // wizard and the continuation promise armed by the first accept is still
      // pending, so the resume message alone carries the flow forward.
      if (
        inPlaceParent ===
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL
      ) {
        const { toLendiCrm } = useIframeBridge();
        toLendiCrm.newDealWithClientCreation.resumeAfterClientCreated({
          goToCart: params?.goToCart ?? true,
        });
        return modalResponse;
      }

      if (
        !inPlaceParent &&
        params?.parentModalType &&
        params?.mode !== "EditClientData"
      ) {
        const { modalClose } = useIframeBridge();
        modalClose.closeByModalType(params.parentModalType, modalResponse);
      }

      if (params?.mode === "EditClientData") {
        return modalResponse;
      }

      const { userInput, simulationId } = useSimulationStore();
      const nextStepParams = {
        goToCart: params?.goToCart ?? true,
        mode: params?.mode,
        userInput: userInput ?? undefined,
        simulationId: simulationId ?? undefined,
        onClientCreationClose: params?.onClientCreationClose,
      };

      // Only goToCart reaches the child in-place; it kept mode/simulationId/userInput from its boot query. The rest mirrors the legacy branch for symmetry.
      const nextStepResponse = inPlaceParent
        ? await continueInPlace(nextStepParams, modalResponse)
        : await openNewDealWithClientCreationModal(nextStepParams);

      await loadCreatedDealIntoSimulation(nextStepResponse);

      return nextStepResponse;
    }

    return modalResponse;
  };

  const breakpoints = useBreakpointsApi();
  const isMobile = computed<boolean>(() => breakpoints.smAndDown);

  return {
    iframeMessageCallback,
    openAddClientModal,
  };
};
